import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Edit, Save } from 'react-feather'
import {
  Box,
  IconButton,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core'
import { ControlledTextField } from './ControlledTextField'

const INPUT_CONTAINER_WIDTH = 80

const useStyles = makeStyles<Theme>((theme) => ({
  input: {
    padding: 8,
  },
  value: {
    paddingRight: theme.spacing(1),
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}))

type FormValues = {
  value: string
}

type Props = {
  value: string
  onChange: (value: string) => Promise<void>
  unit: string
}

export function EditableText({
  value,
  onChange,
  unit,
}: Props): React.ReactElement {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { value },
  })
  const saveButtonRef = useRef(null)
  const classes = useStyles()

  useEffect(() => {
    reset({ value })
  }, [value, reset])

  const onSubmit = async ({ value: newValue }: FormValues) => {
    if (!isEditing) {
      setIsEditing(true)
      return
    }

    try {
      setIsLoading(true)
      await onChange(newValue)
    } finally {
      setIsLoading(false)
      setIsEditing(false)
    }
  }

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // If user has clicked the save button, we don't want to clear the field.
    if (event.relatedTarget === saveButtonRef.current) {
      return
    }

    reset()
    setIsEditing(false)
  }

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // The value is not present on the first render.
    setTimeout(() => event.target.select())
  }

  const textOrEditor = isEditing ? (
    <ControlledTextField
      control={control}
      name="value"
      onBlur={onBlur}
      InputProps={{
        classes: { input: classes.input },
        endAdornment: <Typography>{unit}</Typography>,
      }}
      autoFocus
      onFocus={onFocus}
      disabled={isLoading}
      autoComplete="off"
    />
  ) : (
    <Box display="flex">
      <Typography className={classes.value}>{value}</Typography>
      <Typography>{unit}</Typography>
    </Box>
  )

  const Icon = isEditing ? Save : Edit

  return (
    <Box display="flex" justifyContent="center">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box
            width={INPUT_CONTAINER_WIDTH}
            display="flex"
            justifyContent="center"
          >
            {textOrEditor}
          </Box>

          <Box display="flex" alignItems="center" width={36} height={36} pl={2}>
            <IconButton
              size="small"
              type="submit"
              ref={saveButtonRef}
              disabled={isLoading}
            >
              <Icon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </form>
    </Box>
  )
}
